import { useEffect, useMemo, useState } from "react";
import { apiFetch } from "../lib/apiClient";
import { CertType } from "../lib/types";
import CopyButton from "../components/CopyButton";
import TypeBadge from "../components/TypeBadge";

type ListResponse = { cert_types: CertType[]; total: number };

const TYPES = ["statutory", "mandatory", "other"] as const;
const PAGE_SIZE = 50;

export default function CertTypesPage() {
  const [data, setData] = useState<CertType[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const [types, setTypes] = useState<Set<string>>(new Set());
  const [category, setCategory] = useState<string>("");
  const [page, setPage] = useState(1);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, string> = {};
      if (types.size) query.statutory_type = Array.from(types).join(",");
      if (category.trim()) query.category = category.trim();

      const resp = await apiFetch<ListResponse>("/api/v1/cert_types", { query });
      setData(resp.cert_types);
      setPage(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const categoryOptions = useMemo(() => {
    const src = data ?? [];
    const s = new Set<string>();
    src.forEach(ct => { if (ct.category) s.add(ct.category); });
    return Array.from(s).sort();
  }, [data]);

  function toggle(set: Set<string>, value: string): Set<string> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value); else next.add(value);
    return next;
  }

  function clearFilters() {
    setTypes(new Set());
    setCategory("");
  }

  const activeFilterCount = types.size + (category ? 1 : 0);

  const totalPages = data ? Math.max(1, Math.ceil(data.length / PAGE_SIZE)) : 1;
  const pageSafe = Math.min(page, totalPages);
  const paged = data ? data.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold">Cert types</h1>
        {data && <span className="text-sm text-slate-500">{data.length} total</span>}
        <button onClick={load} className="text-sm border border-slate-300 rounded px-2 py-1" disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
        <span className="ml-auto text-xs text-slate-500">
          Use the <span className="mono">slug</span> as <span className="mono">cert_type_slug</span> when submitting completions.
        </span>
      </div>

      <div className="bg-white border border-slate-200 rounded p-3 flex flex-wrap gap-4 items-start text-sm">
        <FilterChipGroup
          label="Type"
          options={TYPES as readonly string[]}
          selected={types}
          onToggle={v => setTypes(prev => toggle(prev, v))}
        />
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Category</div>
          <select
            className="border border-slate-300 rounded px-2 py-1 text-sm min-w-[220px]"
            value={category}
            onChange={e => setCategory(e.target.value)}
          >
            <option value="">— any —</option>
            {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="ml-auto flex gap-2 items-center">
          <button onClick={load} className="bg-brand-600 text-white rounded px-3 py-1.5 text-sm" disabled={loading}>
            Apply
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-slate-800">
              Clear ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {error && <div className="text-red-600 bg-red-50 border border-red-200 rounded p-3 text-sm">{error}</div>}

      {data && (
        <>
          <div className="bg-white border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left font-medium px-3 py-2">Slug</th>
                  <th className="text-left font-medium px-3 py-2">Name</th>
                  <th className="text-left font-medium px-3 py-2">Type</th>
                  <th className="text-left font-medium px-3 py-2">Category</th>
                  <th className="text-left font-medium px-3 py-2">Recert (months)</th>
                  <th className="text-left font-medium px-3 py-2">Grades</th>
                </tr>
              </thead>
              <tbody>
                {paged.map(ct => (
                  <tr key={ct.slug} className="border-t border-slate-100">
                    <td className="px-3 py-2 mono text-xs align-top">
                      <div className="flex items-center gap-1">
                        <span>{ct.slug}</span>
                        <CopyButton value={ct.slug} />
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div>{ct.name}</div>
                      {ct.scope === "hpac_baseline" ? (
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 text-[10px] rounded bg-slate-100 text-slate-700 border border-slate-200"
                              title="Defined by HPAC centrally.">
                          HPAC
                        </span>
                      ) : (
                        <span className="inline-block mt-0.5 px-1.5 py-0.5 text-[10px] rounded bg-brand-50 text-brand-700 border border-brand-100"
                              title="Defined by your organisation.">
                          Org
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top"><TypeBadge type={ct.statutory_type} /></td>
                    <td className="px-3 py-2 text-slate-500 align-top">{ct.category ?? "—"}</td>
                    <td className="px-3 py-2 align-top">{ct.recertification_frequency ?? "—"}</td>
                    <td className="px-3 py-2 mono text-xs text-slate-500 align-top">{ct.required_for_grades.join(", ") || "—"}</td>
                  </tr>
                ))}
                {paged.length === 0 && (
                  <tr><td colSpan={6} className="p-4 text-sm text-slate-500 text-center">No cert types match.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            page={pageSafe}
            totalPages={totalPages}
            totalItems={data.length}
            pageSize={PAGE_SIZE}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}

function FilterChipGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: readonly string[];
  selected: Set<string>;
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1">
        {options.map(opt => {
          const on = selected.has(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              className={`px-2 py-1 rounded text-xs border ${
                on
                  ? "bg-brand-600 text-white border-brand-600"
                  : "bg-white text-slate-700 border-slate-300 hover:border-brand-500"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Pagination({
  page, totalPages, totalItems, pageSize, onChange,
}: { page: number; totalPages: number; totalItems: number; pageSize: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="text-slate-500">{from}–{to} of {totalItems}</span>
      <div className="ml-auto flex items-center gap-1">
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === 1} onClick={() => onChange(1)}>«</button>
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === 1} onClick={() => onChange(page - 1)}>‹</button>
        <span className="px-2">Page {page} / {totalPages}</span>
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === totalPages} onClick={() => onChange(page + 1)}>›</button>
        <button className="border border-slate-300 rounded px-2 py-1 disabled:text-slate-300" disabled={page === totalPages} onClick={() => onChange(totalPages)}>»</button>
      </div>
    </div>
  );
}
